import { Box, ClickAwayListener, Grid, Grid2, List, Radio } from '@mui/material'
import { Theme, useTheme } from '@mui/material/styles'
import { CheckmarkIcon } from 'assets/icons'
import Button from 'components/buttons/Button'
import Switch from 'components/form/Switch'
import { MiscText } from 'components/login/styled'
import Popup from 'components/popup/Popup'
import PopupSection from 'components/popup/PopupSection'
import { PopupSectionRow } from 'components/popup/PopupSectionRow'
import { set } from 'date-fns'
import { IEventDetails } from 'hooks/useEventDetails'
import i18n from 'i18n'
import { t, use } from 'i18next'
import { ChangeEvent, ReactNode, useEffect, useRef, useState } from 'react'
import { localGroupById } from 'services/db/Queries/GroupQueries'
import LocalStorage from 'services/LocalStorage'
import { AccountDoc, GroupDoc } from 'types/api/changes'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import { highestActiveTier, Tier } from 'utils/api/accounts'
import { TierFeatures } from 'utils/api/group_state'
import { isUserCanOperateEvents } from 'utils/api/groups'
import { is24meApp } from 'utils/appType'
import { isConvertedGroup, isConvertedGroupId } from 'utils/groups'
import { removePrefix } from 'utils/object'
import { isEqual } from 'utils/object'

import { SelectableItem } from './SelectItemWithSearchPopup'

export interface CalendarSectionItem {
  title: string
  items: SelectableItem[]
  sectionIcon?: ReactNode
  availableMultiMode?: boolean
  alwaysVisible?: boolean
}

interface CalendarSelectPopupInterface {
  calendars: CalendarSectionItem[]
  open?: boolean
  setOpen: (b: boolean) => void
  children: ReactNode
  eventHook: IEventDetails | undefined
  group: GroupDoc | undefined
  onCalendarPick: (calendar: SelectableItem) => void
  SupplementaryGroupsIDs: string[]
  account: Model<AccountDoc> | AccountDoc
  onlyCalendarPick?: boolean
}

interface SectionWithCalendarsProps {
  sectionItem: CalendarSectionItem
  SupplementaryGroupsIDs: string[]
  onCalendarPick?: (item: SelectableItem) => void
  multiMode?: boolean
  eventHook?: IEventDetails
  isSyncNotPossible?: boolean
  account?: Model<AccountDoc> | AccountDoc
  group?: GroupDoc
  theme?: Theme
  itemRef?: React.RefObject<HTMLDivElement | null>
  onSupplementaryChange?: (ids: string[]) => void
}

function CalendarSelectPopupContent(props: CalendarSelectPopupInterface) {
  const theme = useTheme()
  const [supplementaryMode, setSupplementaryMode] = useState(
    props.SupplementaryGroupsIDs.length > 0
  )

  const [virtualSupplementaryGroupsIDs, setVirtualSupplementaryGroupsIDs] =
    useState<string[]>(props.SupplementaryGroupsIDs)

  useEffect(() => {
    setVirtualSupplementaryGroupsIDs(props.SupplementaryGroupsIDs)
  }, [props.SupplementaryGroupsIDs])

  function performSuppModeChange(enabled: boolean) {
    setSupplementaryMode(enabled)
    if (enabled) {
      // do nothing
    } else {
      props.eventHook?.setSupplementaryGroupIds([])
    }
  }

  const onSuppModeChanged = (event: ChangeEvent<HTMLInputElement>) => {
    performSuppModeChange(event.target.checked)
  }
  const isUserAllowedToAddEvents = isUserCanOperateEvents(
    props.group,
    LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
  )
  const convertedGroup = isConvertedGroupId(props.eventHook?.groupId)
  const isSyncNotPossible = convertedGroup || !isUserAllowedToAddEvents
  const [itemGroup, setItemGroup] = useState<GroupDoc | undefined>(undefined)

  // Create a ref for the item you want to scroll to
  const itemRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Scroll to the item when the component mounts
    ;(itemRef.current as any)?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'nearest'
    })
  }, [])

  useEffect(() => {
    localGroupById(props.eventHook?.groupId ?? '').then((group) => {
      if (!group) return
      setItemGroup(group)
    })
  }, [props.eventHook?.groupId])

  const isDirty = !isEqual(
    virtualSupplementaryGroupsIDs.sort(),
    props.SupplementaryGroupsIDs.sort()
  )

  useEffect(() => {
    if (!props.open) {
      setVirtualSupplementaryGroupsIDs(props.SupplementaryGroupsIDs)
    }
  }, [props.open])

  return (
    <Box
      sx={{
        overflowY: 'auto',
        overflowX: 'hidden',
        maxWidth: '350px',
        maxHeight: '450px',
        fontSize: '14px'
      }}
    >
      <Grid container direction={'column'}>
        {!props.onlyCalendarPick && (
          <Grid
            container
            direction={'column'}
            sx={{
              paddingLeft: 5,
              paddingRight: 5,
              paddingBottom: 2,
              position: 'sticky',

              zIndex: 10,
              top: 0
            }}
          >
            {isSyncNotPossible || is24meApp() ? (
              <></>
            ) : (
              <div
                style={{
                  backgroundColor: 'var(--color-surface-active)',
                  borderRadius: 8,
                  padding: theme.spacing(1, 4)
                }}
              >
                <PopupSectionRow
                  title={t('syncEventsAcrossCalendars')}
                  neededTier={
                    itemGroup?.OwnerID ===
                    LocalStorage.get(LocalStorageKey.USER_ID)
                      ? Tier.BUSINESS_PLUS
                      : Tier.FREE
                  }
                  tierFeature={TierFeatures.SYNC_WITH_CALENDARS}
                  account={props.account}
                  action={performSuppModeChange.bind(null, true)}
                  value={
                    <Switch
                      onChange={onSuppModeChanged}
                      checked={supplementaryMode}
                    />
                  }
                />
                <MiscText
                  fontSize={'13px'}
                  fontWeight={'400 !important'}
                  lineHeight={1.5}
                  letterSpacing={'0.02em'}
                >
                  {t('updatesToThisEvent')}
                </MiscText>
              </div>
            )}
          </Grid>
        )}
        <List
          sx={{
            width: '100%'
          }}
        >
          {props.calendars
            .filter((section) => {
              if (!supplementaryMode) return true

              return section.availableMultiMode || section.alwaysVisible
            })
            .map((item) => {
              return (
                <SectionWithCalendars
                  key={item.title}
                  sectionItem={item}
                  SupplementaryGroupsIDs={
                    supplementaryMode
                      ? virtualSupplementaryGroupsIDs
                      : props.SupplementaryGroupsIDs
                  }
                  onCalendarPick={
                    supplementaryMode ? undefined : props.onCalendarPick
                  }
                  multiMode={supplementaryMode}
                  eventHook={props.eventHook}
                  isSyncNotPossible={isSyncNotPossible}
                  account={props.account}
                  group={itemGroup}
                  theme={theme}
                  itemRef={itemRef}
                  onSupplementaryChange={setVirtualSupplementaryGroupsIDs}
                />
              )
            })}
        </List>
        {supplementaryMode && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              paddingTop: '0.5rem',
              paddingBottom: '0.1rem',
              background: 'var(--color-surface-hover)',
              borderTop: `1px solid ${theme.palette.border.primary}`,
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Button
              disabled={!isDirty}
              size="xs"
              onClick={() => {
                props.eventHook?.setSupplementaryGroupIds(
                  virtualSupplementaryGroupsIDs
                )
                props.setOpen(false)
              }}
              width="100%"
            >
              {t('apply')}
            </Button>
          </Box>
        )}
      </Grid>
    </Box>
  )
}

function SectionWithCalendars({
  sectionItem,
  SupplementaryGroupsIDs,
  onCalendarPick,
  multiMode,
  eventHook,
  isSyncNotPossible,
  account,
  group,
  theme,
  itemRef,
  onSupplementaryChange
}: SectionWithCalendarsProps) {
  function addOrRemoveItem(item: SelectableItem) {
    if (
      group?.OwnerID === LocalStorage.get(LocalStorageKey.USER_ID) &&
      highestActiveTier(account) < Tier.BUSINESS_PLUS
    ) {
      return
    }

    if (item.notClickable === true) {
      return
    }

    if (item.inherited === true) {
      return
    }

    if (group?._id === item.id) {
      return
    }

    if (!multiMode) return

    if (!SupplementaryGroupsIDs.includes(item.id ?? '')) {
      const newIds = [...SupplementaryGroupsIDs, item.id ?? '']
      if (onSupplementaryChange) {
        onSupplementaryChange(newIds)
      } else {
        eventHook?.setSupplementaryGroupIds(newIds)
      }
    } else {
      const newIds = SupplementaryGroupsIDs.filter((id) => id !== item.id)
      if (onSupplementaryChange) {
        onSupplementaryChange(newIds)
      } else {
        eventHook?.setSupplementaryGroupIds(newIds)
      }
    }
  }

  return (
    <PopupSection title={sectionItem.title} titleIcon={sectionItem.sectionIcon}>
      <List
        sx={{
          width: '100%',
          paddingTop: 0
        }}
      >
        {sectionItem.items.map((item) => {
          const isSelected =
            SupplementaryGroupsIDs.includes(item.id ?? '') ||
            group?._id === item.id ||
            item.inherited === true
          const isCurrentCalendar =
            item.id === eventHook?.getValues('ThirdPartyID') ||
            item.id === eventHook?.groupId
          const isDisabled =
            (item.notClickable && !multiMode && item.id !== group?._id) ||
            (item.notClickable && multiMode)
          const isLockedItem =
            isDisabled ||
            item.inherited === true ||
            (multiMode === true && isCurrentCalendar)
          const disabledBackgroundColor =
            theme?.palette.mode === 'dark' ? '#000000' : 'rgba(0, 0, 0, 0.1)'
          const subtitle = item.inherited
            ? t('linkedCalendar')
            : item.notClickable
              ? t('calendarCannotBeSelected')
              : item.subTitle?.trim()
                ? item.subTitle
                : isSelected
                  ? t('tap_unselect')
                  : t('tap_select')

          return (
            <Grid2
              ref={
                !multiMode &&
                (item.id === removePrefix(eventHook?.groupId ?? '') ||
                  item.id === eventHook?.thirdPartyId)
                  ? itemRef
                  : null
              }
              width={'100%'}
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'space-between'}
              alignItems={'center'}
              sx={{
                cursor: isLockedItem ? 'not-allowed' : 'pointer',
                backgroundColor: isDisabled ? disabledBackgroundColor : '',
                padding: '0px 8px',
                margin: '4px 0px',
                borderRadius: '8px',
                minHeight: multiMode ? '52px' : '30px',
                '&:hover': {
                  backgroundColor: !isDisabled
                    ? 'var(--color-surface-hover)'
                    : undefined
                }
              }}
              key={item.id}
            >
              <Grid
                onClick={
                  isLockedItem
                    ? undefined
                    : multiMode
                      ? addOrRemoveItem.bind(null, item)
                      : onCalendarPick?.bind(null, item)
                }
                container
                display={'flex'}
                direction={'column'}
              >
                <Box
                  display={'flex'}
                  color={'var(--color-text-primary)'}
                  flexDirection={'row'}
                  alignItems={'baseline'}
                  gap={1}
                  id={`change-calendar-button-${item.title}`}
                >
                  {item.title}
                  {!multiMode && isCurrentCalendar && (
                    <Box
                      component={'span'}
                      sx={{
                        fontSize: '13px',
                        fontWeight: '400 !important',
                        lineHeight: 1.5,
                        letterSpacing: '0.02em',
                        color:
                          item.subTitleColor ?? 'var(--color-text-secondary)'
                      }}
                    >
                      {t('primaryBadge')}
                    </Box>
                  )}
                </Box>
                {multiMode ? (
                  <MiscText
                    fontSize={'13px'}
                    fontWeight={'400 !important'}
                    lineHeight={1.5}
                    letterSpacing={'0.02em'}
                    color={item.subTitleColor ?? 'var(--color-text-secondary)'}
                  >
                    {subtitle}
                  </MiscText>
                ) : (
                  <></>
                )}
              </Grid>

              {isSyncNotPossible || !multiMode ? (
                (multiMode && isCurrentCalendar) ||
                (!multiMode && isSelected) ? (
                  <CheckmarkIcon style={{ marginRight: 4 }} />
                ) : (
                  item.rightIcon
                )
              ) : multiMode && sectionItem.availableMultiMode ? (
                <Radio
                  sx={{ padding: 1 }}
                  checked={isSelected}
                  disabled={item.notClickable === true}
                  onClick={addOrRemoveItem.bind(null, item)}
                />
              ) : (
                item.rightIcon
              )}
            </Grid2>
          )
        })}
      </List>
    </PopupSection>
  )
}

export default function CalendarSelectPopup(
  props: CalendarSelectPopupInterface
) {
  return (
    <Popup
      setOpen={props.setOpen}
      open={props.open}
      width="350px"
      content={<CalendarSelectPopupContent {...props} />}
      placement={i18n.dir() === 'ltr' ? 'right' : 'left'}
      fallbackPlacements={
        i18n.dir() === 'ltr' ? ['right', 'left', 'top'] : ['left', 'top']
      }
    >
      {props.children}
    </Popup>
  )
}
